<!--
  - ===========================================================================
  - [StrongGate Limited] [http://www.stronggate.com]
  - Copyright (c) 2026. All rights Reserved.
  - Unauthorized access, use, dissemination, distribution, reproduction of this
  - file, or a portion of, in any form is prohibited and unlawful. If you receive
  - this file, and you are not an authorized company employee, or you believe you
  - have received this file in error, please notify the sender (or originator)
  - and promptly delete this file.
  -
  - Project:
  - File: ztn-dialog.vue
  - Created by: chan.ying
  - Create Date: 2026-04-10 20:10
  - Version Number: 1.0
  - Version Description:
  -
  - =============================================================================
  -->

<template>
  <transition name="ztn-dialog-fade">
    <div v-if="visible" class="ztn-dialog__wrapper" @click.self="handleWrapperClick">
      <div class="ztn-dialog" :style="{ width, height }">
        <!-- Header -->
        <div class="ztn-page-h600-strong ztn-dialog__header">
          <slot name="header">
            <span class="ztn-dialog__title">{{ title }}</span>
          </slot>
          <button v-if="showClose" class="ztn-dialog__close" @click="handleClose">×</button>
        </div>

        <!-- Body -->
        <div class="ztn-dialog__body">
          <slot></slot>
        </div>

        <!-- Footer -->
        <div class="ztn-dialog__footer" v-if="$slots.footer">
          <slot name="footer"></slot>
        </div>
        <div v-else class="ztn-dialog__footer">
          <ztn-cancel-button v-if="showCancelButton" :button-text="cancelText" @click="handleCancel"/>
          <ztn-button
              :variant="destructive ? 'danger' : 'primary'"
              :button-text="confirmText"
              @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import ZtnCancelButton from "@/components/input/ztn-cancel-button.vue";
import ZtnButton from "@/components/input/ztn-button.vue";

export default {
  name: 'ztn-dialog',
  components: {ZtnCancelButton, ZtnButton},
  props: {
    modelValue: {type: Boolean, default: false},
    title: {type: String, default: 'Title'},
    width: {type: String, default: '520px'},
    height: {type: String},
    showClose: {type: Boolean, default: true},
    closeOnClickModal: {type: Boolean, default: true},
    showCancelButton: {type: Boolean, default: true},
    cancelText: {type: String, default: 'Cancel'},
    confirmText: {type: String, default: 'Confirm'},
    destructive: {type: Boolean, default: false}
  },
  data() {
    return {
      visible: this.modelValue
    }
  },
  emits: ['update:modelValue', 'close', 'cancel', 'confirm'],
  watch: {
    modelValue(newVal) {
      this.visible = newVal
    },
    visible(newVal) {
      this.$emit('update:modelValue', newVal)
      document.body.style.overflow = newVal ? 'hidden' : ''
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleEsc)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEsc)
    document.body.style.overflow = ''
  },
  methods: {
    handleClose() {
      this.visible = false
      this.$emit('close')
    },
    handleCancel() {
      this.handleClose()
      this.$emit('cancel')
    },
    handleConfirm() {
      this.$emit('confirm')
      this.handleClose()
    },
    handleWrapperClick() {
      // if (this.closeOnClickModal) this.handleClose()
    },
    handleEsc(e) {
      if (e.key === 'Escape' && this.visible) {
        this.handleClose()
      }
    }
  }
}
</script>

<style scoped>
.ztn-dialog__wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-dialog);
}

.ztn-dialog {
  background: var(--bg-background);
  border-radius: var(--radius-lg);
  box-shadow: var(--elevation-md);
  max-width: 96vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ztn-dialog__header {
  padding: 20px var(--space-5) var(--space-4);
  border-bottom: 1px solid var(--line-box-border-3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--body-color-1);
  flex-shrink: 0;
}

.ztn-dialog__title {
  flex: 1;
}

.ztn-dialog__close {
  font-size: var(--fs-24);
  line-height: 1;
  color: var(--body-aux-1);
  background: none;
  border: none;
  cursor: pointer;
}

.ztn-dialog__close:hover {
  color: var(--body-color-h);
}

.ztn-dialog__body {
  padding: var(--space-5);
  font-size: var(--fs-14);
  color: var(--body-color-1);
  line-height: 1.6;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.ztn-dialog__footer {
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--line-box-border-3);
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-3);
  flex-shrink: 0;
}

.ztn-dialog-fade-enter-active,
.ztn-dialog-fade-leave-active {
  transition: opacity var(--motion-duration-sm) var(--motion-easing);
}

.ztn-dialog-fade-enter-active .ztn-dialog,
.ztn-dialog-fade-leave-active .ztn-dialog {
  transition: opacity var(--motion-duration-md) var(--motion-easing),
              transform var(--motion-duration-md) var(--motion-easing);
}

.ztn-dialog-fade-enter-from,
.ztn-dialog-fade-leave-to {
  opacity: 0;
}

.ztn-dialog-fade-enter-from .ztn-dialog,
.ztn-dialog-fade-leave-to .ztn-dialog {
  opacity: 0;
  transform: translateY(8px);
}
</style>